import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../utils/api';
import { UpdateProfileRequest } from '../../types/api';
import styles from './ProfileCompletionScreen.module.css';
import { Loader } from '../../components/Loader';

interface ProfileCompletionScreenProps {
  prefilledCountry?: string;
}

export const ProfileCompletionScreen: React.FC<ProfileCompletionScreenProps> = ({ prefilledCountry }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Get country from location state (passed from login/attestation) or props
  const countryFromState = location.state && 'country' in location.state 
    ? (location.state as { country?: string }).country 
    : undefined;
  const initialCountry = countryFromState || prefilledCountry || '';
  
  // Form state - required and optional fields
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    job_title: '',
    x_account: '',
    linkedin: '',
    country: initialCountry
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  // Update country when it's available from state or props
  useEffect(() => {
    const countryToUse = countryFromState || prefilledCountry;
    if (countryToUse && !formData.country) {
      setFormData(prev => ({ ...prev, country: countryToUse }));
    }
  }, [countryFromState, prefilledCountry]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const userProfile = await apiService.getUserProfile();
      
      // Get country from state/props first, then from profile, then empty string
      const countryToUse = countryFromState || prefilledCountry || userProfile.country || '';
      
      // Populate form with existing data
      setFormData({
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        company_name: userProfile.company_name || '',
        job_title: userProfile.job_title || '',
        x_account: userProfile.x_account || '',
        linkedin: userProfile.linkedin || '',
        country: countryToUse
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (): boolean => {
    return !!(
      formData.first_name?.trim() &&
      formData.last_name?.trim() &&
      formData.company_name?.trim() &&
      formData.country?.trim()
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const updateData: UpdateProfileRequest = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        company_name: formData.company_name.trim(),
        job_title: formData.job_title.trim() || undefined,
        x_account: formData.x_account.trim() || undefined,
        linkedin: formData.linkedin.trim() || undefined,
        country: formData.country.trim()
      };

      await apiService.updateUserProfile(updateData);
      
      // Redirect to deals page after successful save
      navigate('/deals');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Loader fullscreen />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Complete Your Profile</h1>
      <p className={styles.description}>
        Please provide the following information to continue.
      </p>

      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Personal Information</h2>
          
          <div className={styles.formGroup}>
            <label htmlFor="first_name">
              First Name <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              className={styles.input}
              required
              autoFocus
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="last_name">
              Last Name <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              className={styles.input}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="country">
              Country <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              className={styles.input}
              required
              readOnly={!!(countryFromState || prefilledCountry)}
              style={(countryFromState || prefilledCountry) ? { backgroundColor: 'var(--tg-theme-secondary-bg-color, #f3f4f6)', cursor: 'not-allowed' } : {}}
            />
            {(countryFromState || prefilledCountry) && (
              <div className={styles.hint}>This field was prefilled from your attestation</div>
            )}
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Professional Information</h2>
          
          <div className={styles.formGroup}>
            <label htmlFor="company_name">
              Company Name <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="company_name"
              name="company_name"
              value={formData.company_name}
              onChange={handleInputChange}
              className={styles.input}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="job_title">
              Job Title
            </label>
            <input
              type="text"
              id="job_title"
              name="job_title"
              value={formData.job_title}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="x_account">
              X (Twitter) Account
            </label>
            <input
              type="text"
              id="x_account"
              name="x_account"
              value={formData.x_account}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="linkedin">
              LinkedIn
            </label>
            <input
              type="text"
              id="linkedin"
              name="linkedin"
              value={formData.linkedin}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="submit"
            disabled={saving || !validateForm()}
            className={`${styles.submitButton} ${validateForm() ? styles.activeButton : ''}`}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

